import type { components } from "@medimo/core-api/internal/openapi";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

type Consult = components["schemas"]["ConsultReadModel"];
type ConsultLog = components["schemas"]["ConsultLogReadModel"];
type ConsultConsultLog = {
  consult?: Partial<Consult>;
  consultLog?: Partial<ConsultLog>;
  viaPreconsult?: boolean;
};

type UseGlobalConsultConsultLog = {
  sessionGeneration: number;
  consult: Partial<Consult>;
  consultLog: Partial<ConsultLog>;
  liveTranscriptionContent: string;
  viaPreconsult: boolean;
  isDeleting: boolean;
  create: (data: ConsultConsultLog | null) => number;
  update: (data: ConsultConsultLog | null) => void;
  updateLiveTranscriptionContent: (content: string) => void;
  setIsDeleting: (value: boolean) => void;
  isCurrentSession: (sessionGeneration: number) => boolean;
  resetIfCurrentSession: (sessionGeneration: number) => boolean;
  reset: () => void;
};

// 無音録音の空枠は残すが、削除済み録音のIDの欠番では配列を広げない。
export const setRecordingTranscription = (
  transcriptions: readonly string[] | null | undefined,
  recordingNumber: number,
  content: string,
): string[] =>
  Array.from(
    { length: Math.max(transcriptions?.length ?? 0, recordingNumber) },
    (_, index) =>
      index === recordingNumber - 1 ? content : (transcriptions?.[index] ?? ""),
  );

export const useGlobalTranscribingConsultConsultLog =
  create<UseGlobalConsultConsultLog>()(
    devtools(
      immer((set, get) => ({
        sessionGeneration: 0,
        consult: {},
        consultLog: {},
        liveTranscriptionContent: "",
        viaPreconsult: false,
        isDeleting: false,
        create(next) {
          set((state) => {
            // 同じ診察・versionを再開する場合も、前の非同期処理とは別セッションとして扱う。
            state.sessionGeneration += 1;
            state.consult = next ? (next.consult ?? {}) : {};
            state.consultLog = next ? (next.consultLog ?? {}) : {};
            state.liveTranscriptionContent = "";
            state.viaPreconsult = next ? (next.viaPreconsult ?? false) : false;
            state.isDeleting = false;
          });
          return get().sessionGeneration;
        },
        update(next) {
          set((state) => {
            state.consult = next ? { ...state.consult, ...next.consult } : {};
            state.consultLog = next
              ? { ...state.consultLog, ...next.consultLog }
              : {};
          });
        },
        updateLiveTranscriptionContent(content) {
          set((state) => {
            state.liveTranscriptionContent = content;
          });
        },
        setIsDeleting(value) {
          set((state) => {
            state.isDeleting = value;
          });
        },
        isCurrentSession(sessionGeneration) {
          return get().sessionGeneration === sessionGeneration;
        },
        resetIfCurrentSession(sessionGeneration) {
          if (get().sessionGeneration !== sessionGeneration) return false;
          set((state) => {
            // 世代を先に進め、同じセッションから遅れて到着する二重cleanupも無効化する。
            state.sessionGeneration += 1;
            state.consult = {};
            state.consultLog = {};
            state.liveTranscriptionContent = "";
            state.viaPreconsult = false;
            state.isDeleting = false;
          });
          return true;
        },
        reset() {
          set((state) => {
            // 明示的な破棄後に古い非同期処理が戻ってきても、次のstateを所有できないようにする。
            state.sessionGeneration += 1;
            state.consult = {};
            state.consultLog = {};
            state.liveTranscriptionContent = "";
            state.viaPreconsult = false;
            state.isDeleting = false;
          });
        },
      })),
    ),
  );
